import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Notification, NotificationCategory } from '../types';
import { markAllAsRead } from '../lib/notificationService';
import NotificationCard from './NotificationCard';

interface NotificationListProps {
    userId: string;
    notifications: Notification[];
    onRefresh?: () => void;
    onClose?: () => void; // Optional: to close dropdown etc on click
}

const NotificationList: React.FC<NotificationListProps> = ({
    userId,
    notifications,
    onRefresh,
    onClose
}) => {
    const [activeFilter, setActiveFilter] = useState<NotificationCategory | 'ALL'>('ALL');
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);

    const unreadCount = notifications.filter(n => !n.isRead).length;

    // Filter notifications
    const filteredNotifications = notifications.filter(n => {
        if (activeFilter !== 'ALL' && n.category !== activeFilter) return false;
        if (showUnreadOnly && n.isRead) return false;
        return true;
    });

    const handleMarkAllRead = async () => {
        await markAllAsRead(userId);
        onRefresh?.();
    };

    const categoryFilters: Array<{ key: NotificationCategory | 'ALL'; label: string; icon: string }> = [
        { key: 'ALL', label: 'Tümü', icon: '📬' },
        { key: NotificationCategory.MARKETPLACE, label: 'Pazar', icon: '📦' },
        { key: NotificationCategory.ORDERS, label: 'Siparişler', icon: '💰' },
        { key: NotificationCategory.FINANCIAL, label: 'Finans', icon: '💸' },
        { key: NotificationCategory.SOCIAL, label: 'Sosyal', icon: '👥' },
        { key: NotificationCategory.SECURITY, label: 'Güvenlik', icon: '🔒' },
        { key: NotificationCategory.ADMIN, label: 'Admin', icon: '👮' },
        { key: NotificationCategory.SYSTEM, label: 'Sistem', icon: '⚙️' },
    ];

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-950 transition-colors duration-300">
            {/* Header */}
            <div className="p-4 border-b border-gray-100 dark:border-white/10 bg-white/50 dark:bg-gray-900/50 sticky top-0 z-10 backdrop-blur-md">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-xl font-black text-brand-clay dark:text-white tracking-tight">Bildirimler</h3>
                        {unreadCount > 0 && (
                            <p className="text-[10px] font-bold text-brand-terracotta uppercase tracking-wider mt-1">
                                {unreadCount} Yeni Bildirim
                            </p>
                        )}
                    </div>
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            className="px-3 py-1.5 rounded-full bg-brand-terracotta/10 text-[10px] font-black text-brand-terracotta uppercase tracking-widest hover:bg-brand-terracotta hover:text-white transition-all active:scale-95"
                        >
                            Tümünü Oku
                        </button>
                    )}
                </div>

                {/* Category Filters */}
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
                    {categoryFilters.map((filter) => {
                        const count = notifications.filter(
                            n => filter.key === 'ALL' ? true : n.category === filter.key
                        ).filter(n => !n.isRead).length;

                        return (
                            <button
                                key={filter.key}
                                onClick={() => setActiveFilter(filter.key)}
                                className={`
                                    flex-shrink-0
                                    px-4 py-2
                                    rounded-xl
                                    text-[11px] font-bold
                                    flex items-center gap-2
                                    transition-all duration-300
                                    ${activeFilter === filter.key
                                        ? 'bg-brand-clay dark:bg-white dark:text-gray-950 text-white shadow-lg shadow-brand-clay/20 scale-105'
                                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                    }
                                `}
                            >
                                <span className="text-base">{filter.icon}</span>
                                <span>{filter.label}</span>
                                {count > 0 && (
                                    <span className={`w-1.5 h-1.5 rounded-full ${activeFilter === filter.key ? 'bg-white' : 'bg-brand-terracotta'} animate-pulse`} />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Unread Only Toggle */}
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-white/10 flex items-center justify-between shrink-0">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Sadece Okunmamışlar</span>
                <button
                    onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                    className={`
                        relative inline-flex h-5 w-9 items-center rounded-full transition-colors
                        ${showUnreadOnly ? 'bg-brand-terracotta' : 'bg-gray-300'}
                    `}
                >
                    <span
                        className={`
                            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                            ${showUnreadOnly ? 'translate-x-[1.125rem]' : 'translate-x-0.5'}
                        `}
                    />
                </button>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-950 min-h-[300px]">
                {filteredNotifications.length === 0 ? (
                    <div className="p-12 text-center flex flex-col items-center justify-center h-full">
                        <div className="text-5xl mb-3">🔔</div>
                        <p className="text-gray-500 font-medium">
                            {showUnreadOnly ? 'Okunmamış bildirim yok' : 'Henüz bildirim yok'}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100 dark:divide-white/5">
                        {filteredNotifications.map((notification) => (
                            <NotificationCard
                                key={notification.id}
                                notification={notification}
                                onRead={() => onRefresh?.()}
                                onAction={(action) => {
                                    onClose?.(); // Close dropdown if action taken
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationList;
